import React, { ReactElement, useEffect } from 'react'
import { useAppSelector } from 'store/hooks'
import {
  IP,
  useDnsLatencyQueryWithTimeout,
  useGetOriginIpv4Query,
  useGetOriginIpv6QueryWithTimeout,
  useLazyReloadConfigQuery,
  useNewIpv4Query,
  useNewIpv6QueryWithTimeout,
  useNullRouteCheckQueryWithTimeout,
  useProtocolAndResolversDetectionQuery,
  useProxyAuthCheckQuery,
  useProxyLatencyQueryWithTimeout,
  useVerifyControlDUsageQuery,
} from 'store/api/configurationStatus'
import { useGetProxiesNoAuthQuery } from 'store/api/proxies'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { v4, v6 } from 'is-ip'
import padStart from 'lodash/padStart'
import padEnd from 'lodash/padEnd'
import OrgStaticPage from '../Organization/HomePage/OrgStaticPage'
import { StaticPageBody } from '../NewStaticPage'
import { Flex, Text } from 'theme-ui'
import CopyToClipboard from '../CopyToClipboard'
import InfoButton from 'ui/InfoButton'
import { DOCS_CONTROL_D_DOMAIN } from 'gatsby-env-variables'
import ExternalLink from 'ui/ExternalLink/ExternalLink.base'
import { Svg } from 'ui'
import GoodLatencyIcon from 'images/status/good-latency.svg'
import OkLatencyIcon from 'images/status/ok-latency.svg'
import BadLatencyIcon from 'images/status/bad-latency.svg'
import CurrentIpAddressGroup from './CurrentIpAddressGroup'
import DnsGroup from './DnsGroup'
import ProxyGroup from './ProxyGroup'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'
import { StatusPageTitle } from './StatusPageTitle'
import FrequentlyAskedQuestions from 'components/FrequentlyAskedQuestions'
import { frequentlyAskedQuestions } from './frequentlyAskedQuestions'

export enum ResolverType {
  legacy = 'Legacy',
  doh = 'DNS-over-HTTPS',
  dot = 'DNS-over-TLS',
  doq = 'DNS-over-QUIC',
  doh3 = 'DNS-over-HTTPS/3',
}

export const notAvailable = 'N/A'
const getIpText = (ip?: IP): string => {
  const org = ip?.org ? ` (${ip.org})` : ''
  return ip?.ip ? `${ip?.ip}${org}` : notAvailable
}
export const getMsText = (value?: string): string => (value ? `${value}ms` : notAvailable)
const getYesNoText = (value?: boolean) => (value ? 'Yes' : 'No')

const extractFirst4PartsFromIpv6 = ipv6 => ipv6.split(':', 4).join(':')

export default function StatusPage(): ReactElement {
  const sessionToken = useAppSelector(s => s.persistData.sessionToken)
  const [reloadConfig] = useLazyReloadConfigQuery()
  const {
    data: originIPv4,
    isUninitialized: isOriginIpv4Uninitialized,
    isLoading: isOriginIpv4Loading,
  } = useGetOriginIpv4Query('')
  const {
    data: originIPv6,
    isUninitialized: isOriginIpv6Uninitialized,
    isLoading: isOriginIpv6Loading,
  } = useGetOriginIpv6QueryWithTimeout()
  const {
    data: ipControlD,
    isUninitialized: isIpControlDUninitialized,
    isLoading: isIpControlDLoading,
  } = useVerifyControlDUsageQuery('')
  const {
    data: detectionInfo,
    isUninitialized: isDetectionInfoUninitialized,
    isLoading: isDetectionInfoLoading,
  } = useProtocolAndResolversDetectionQuery(!!sessionToken)
  const {
    data: newIPv4,
    isLoading: isNewIpv4Loading,
    isUninitialized: isNewIpv4Uninitialized,
  } = useNewIpv4Query('')
  const {
    data: newIPv6,
    isLoading: isNewIpv6Loading,
    isUninitialized: isNewIpv6Uninitialized,
  } = useNewIpv6QueryWithTimeout()
  const {
    data: dnsLatencyData,
    isUninitialized: isDnsLatencyUninitialized,
    isLoading: isDnsLatencyLoading,
  } = useDnsLatencyQueryWithTimeout()
  const {
    data: dnsLatencyData2,
    isUninitialized: isDnsLatency2Uninitialized,
    isLoading: isDnsLatencyLoading2,
  } = useDnsLatencyQueryWithTimeout()

  const {
    data: proxyLatencyData,
    isUninitialized: isProxyLatencyUninitialized,
    isLoading: isProxyLatencyLoading,
  } = useProxyLatencyQueryWithTimeout()
  const {
    data: proxyLatencyData2,
    isUninitialized: isProxyLatency2Uninitialized,
    isLoading: isProxyLatencyLoading2,
  } = useProxyLatencyQueryWithTimeout()

  const {
    data: isProxyAuthorized,
    isUninitialized: isProxyAuthUninitialized,
    isLoading: isProxyAuthLoading,
  } = useProxyAuthCheckQuery('')
  const { isLoading: proxiesLoading } = useGetProxiesNoAuthQuery('')
  const { error: nullRoutedError } = useNullRouteCheckQueryWithTimeout()

  const finalDnsLatency =
    +(dnsLatencyData?.rtt || 0) > +(dnsLatencyData2?.rtt || 0) ? dnsLatencyData2 : dnsLatencyData

  const finalProxyLatency =
    +(proxyLatencyData?.rtt || 0) > +(proxyLatencyData2?.rtt || 0)
      ? proxyLatencyData2
      : proxyLatencyData

  const {
    rtt: dnsLatency,
    ip: dnsIp,
    host: dnsHost,
  } = finalDnsLatency || { host: '', rtt: '', ip: '' }
  const {
    rtt: proxyLatency,
    ip: proxyIp,
    host: proxyHost,
  } = finalProxyLatency || { host: '', rtt: '', ip: '' }

  const isNullRouted =
    ((nullRoutedError as FetchBaseQueryError)?.data as { error: { message: string } })?.error
      .message === 'Prefix blocked'
  const shouldShowProxyMismatchError =
    (v4(proxyIp) && v4(dnsIp) && proxyIp !== dnsIp) ||
    (v6(proxyIp) &&
      v6(dnsIp) &&
      extractFirst4PartsFromIpv6(proxyIp) !== extractFirst4PartsFromIpv6(dnsIp))
  const shouldShowErrorMessage = isNullRouted || shouldShowProxyMismatchError

  // Example report:
  // Control D Troubleshooting - Tue, 15 Nov 2022 17:28:06 UTC
  // -------------------------------------------------------------
  // IPv4 Address      |  204.237.18.100 (Xplornet Communications)
  // IPv6 Address      |  N/A
  // Using Control D   |  DVA
  // Resolver          |  1ndp7btybjh
  // DNS Protocol      |  DNS-over-HTTPS
  // DNS Latency       |  39.62ms

  const getClipboardFormattedTroubleshootingDetails = (): string => {
    const tableTitle = `Control D Troubleshooting - ${new Date()
      .toUTCString()
      .replace('GMT', 'UTC')}`
    const tableColDivider = '  |  '
    const tableData: [string, string][] = [
      ['IPv4 Address', originIPv4?.ip || notAvailable],
      [
        'IPv4 ISP',
        `${originIPv4?.asn || notAvailable} ${
          originIPv4?.org && originIPv4?.country
            ? `(${originIPv4?.org}, ${originIPv4?.country})`
            : ''
        }`,
      ],
      ['IPv6 Address', originIPv6?.ip || notAvailable],
      [
        'IPv6 ISP',
        `${originIPv6?.asn || notAvailable} ${
          originIPv6?.org && originIPv6?.country
            ? `(${originIPv6?.org}, ${originIPv6?.country})`
            : ''
        }`,
      ],
      ['Using Control D', ipControlD?.pop || 'No'],
      [
        'Resolver',
        detectionInfo?.uid
          ? `${detectionInfo?.uid}${detectionInfo?.device ? `:${detectionInfo?.device}` : ''}`
          : notAvailable,
      ],
      [
        'DNS Protocol',
        detectionInfo?.protocol ? ResolverType[detectionInfo?.protocol] : notAvailable,
      ],
      ['DNS Latency', getMsText(dnsLatency)],
      ['DNS Host', dnsHost || notAvailable],
      ['DNS Source IP', dnsIp || notAvailable],
      ['Proxy Authorized', getYesNoText(isProxyAuthorized)],
      ['Null Routed', getYesNoText(isNullRouted)],
      ['Proxy Latency', getMsText(proxyLatency)],
      ['Proxy Host', proxyHost || notAvailable],
      ['Proxy Source IP', proxyIp || notAvailable],
    ]

    if (newIPv4?.ip && newIPv4?.ip !== originIPv4?.ip) {
      tableData.push(['New IPv4 Address', getIpText(newIPv4)])
    }

    if (newIPv6?.ip && newIPv6?.ip !== originIPv6?.ip) {
      tableData.push(['New IPv6 Address', getIpText(newIPv6)])
    }

    const maxTitleSize = Math.max(...tableData.map(row => row[0].length))
    const maxValueSize = Math.max(...tableData.map(row => row[1].length))
    const maxLineSize = Math.max(
      tableTitle.length,
      maxTitleSize + tableColDivider.length + maxValueSize,
    )
    const tableTitleDivider = padStart('', maxLineSize, '-')
    const tableContent = `${tableData
      .map(([title, value]) => [padEnd(title, maxTitleSize), value].join(tableColDivider))
      .join('\n')}`

    return [tableTitle, tableTitleDivider, tableContent].join('\n')
  }

  useEffect(() => {
    if (detectionInfo?.uid) {
      // ctrld config reload request
      reloadConfig(detectionInfo.uid)
    }
  }, [detectionInfo?.uid, reloadConfig])

  return (
    <OrgStaticPage hasMaxWidth={true} intraPageLinks={[{ text: 'Status', to: '/status' }]}>
      <StaticPageBody
        sxChildrenWrapper={{ width: '100%', maxWidth: '100rem', zIndex: 'zIndex1' }}
        sxBody={{
          px: ['1.6rem', 0],
          mt: ['3.2rem', '10rem'],
        }}
      >
        <Flex
          sx={{
            width: '100%',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '100rem',
            mb: ['4rem', '8rem'],
          }}
        >
          <StatusPageTitle shouldShowErrorBox={shouldShowErrorMessage} />
          <Flex
            sx={{
              width: '100%',
              mt: ['3.2rem', shouldShowErrorMessage ? '2.4rem' : '4.8rem'],
              mb: ['4.8rem', shouldShowErrorMessage ? '2.4rem' : '4.8rem'],
              alignItems: 'center',
              justifyContent: 'center',
              gap: ['2.4rem', '1.6rem', 'none'],
            }}
          >
            <Flex sx={{ gap: ['1.6rem', '1.6rem'] }}>
              <CopyToClipboard
                smallIcon
                variant="gradientButton"
                data={getClipboardFormattedTroubleshootingDetails()}
                fillSvg="title"
                sx={{
                  p: '1.2rem',
                  flexDirection: 'row-reverse',
                }}
              >
                <Text
                  sx={{
                    fontSize: '1.6rem',
                    fontWeight: 'bold',
                    ml: '0.4rem',
                  }}
                  tabIndex={0}
                >
                  Copy results
                </Text>
              </CopyToClipboard>
              <InfoButton
                testId="status-page-info-button"
                onInfoIconClick={() => {
                  window.open(
                    `${DOCS_CONTROL_D_DOMAIN}/docs/status-page`,
                    '_blank',
                    'noopener, noreferrer',
                  )
                }}
                hasBackgroundOnHover
                fillSvg="lightSteelBlue"
                sx={{
                  width: '4.7rem',
                  height: '4.7rem',
                  border: '1px solid',
                  borderColor: 'cyan60',
                  backgroundImage:
                    'linear-gradient(0deg, rgba(118, 219, 255, 0.1) 0%, rgba(118, 219, 255, 0.1) 100%), #010818',
                  '& svg': {
                    width: '2.4rem',
                    height: '2.4rem',
                  },
                }}
              />
            </Flex>
          </Flex>
          <>
            {shouldShowErrorMessage && (
              <Flex
                sx={{
                  mb: '2.4rem',
                  borderRadius: '4.5rem',
                  backgroundColor: 'pomegranate15',
                  color: 'pomegranate',
                  p: '1.6rem',
                  fontWeight: 'bold',
                }}
              >
                {isNullRouted && 'Your IP is blocked by the anti-abuse system. DNS will not work.'}
                {shouldShowProxyMismatchError && !isNullRouted && (
                  <Text>
                    Multiple IPs were detected across DNS and Proxy scopes. This can cause issues.{' '}
                    <ExternalLink
                      to={`${DOCS_CONTROL_D_DOMAIN}/docs/ip-mismatch-between-dns-and-proxy`}
                      sx={{
                        color: 'pomegranate50',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Learn more
                    </ExternalLink>
                  </Text>
                )}
              </Flex>
            )}
            <Flex
              sx={{
                position: 'relative',
                width: ['100%', '67.2rem', '100rem'],
                flexDirection: 'column',
                overflowWrap: 'anywhere',
                boxShadow: '0 16px 24px 0 #0d0e13',
                alignItems: 'center',
                gap: '4.8rem',
                color: 'lightSteelBlue',
                fontSize: '1.5rem',
              }}
            >
              <CurrentIpAddressGroup
                originIPv4={originIPv4}
                originIPv6={originIPv6}
                isOriginIpv4Loading={isOriginIpv4Uninitialized || isOriginIpv4Loading}
                isOriginIpv6Loading={isOriginIpv6Uninitialized || isOriginIpv6Loading}
              />
              <DnsGroup
                ipControlD={ipControlD}
                detectionInfo={detectionInfo}
                dnsLatency={dnsLatency}
                dnsHost={dnsHost}
                dnsIp={dnsIp}
                isDetectionInfoLoading={isDetectionInfoUninitialized || isDetectionInfoLoading}
                isDnsLatencyLoading={isDnsLatencyUninitialized || isDnsLatencyLoading}
                isDnsLatencyLoading2={isDnsLatency2Uninitialized || isDnsLatencyLoading2}
                isIpControlDLoading={isIpControlDUninitialized || isIpControlDLoading}
              />
              <ProxyGroup
                isProxyAuthorized={isProxyAuthorized}
                proxyLatency={proxyLatency}
                proxyHost={proxyHost}
                proxyIp={proxyIp}
                newIPv4={newIPv4}
                newIPv6={newIPv6}
                originIPv4={originIPv4}
                originIPv6={originIPv6}
                isProxyLatencyLoading={isProxyLatencyUninitialized || isProxyLatencyLoading}
                isProxyLatencyLoading2={isProxyLatency2Uninitialized || isProxyLatencyLoading2}
                isProxyAuthLoading={isProxyAuthUninitialized || isProxyAuthLoading}
                proxiesLoading={proxiesLoading}
                isNewIpv4Loading={isNewIpv4Uninitialized || isNewIpv4Loading}
                isNewIpv6Loading={isNewIpv6Uninitialized || isNewIpv6Loading}
              />
            </Flex>
            <FrequentlyAskedQuestions
              sx={{
                width: ['100%', '67.2rem', '100rem'],
                px: 0,
                mt: ['8rem', '10rem'],
              }}
              data={frequentlyAskedQuestions}
            />
          </>
        </Flex>
      </StaticPageBody>
    </OrgStaticPage>
  )
}

function getLatencyStatus(latency: number): { icon: React.ElementType; color: string } {
  if (latency <= 50) {
    return {
      icon: GoodLatencyIcon,
      color: 'successOpaque',
    }
  } else if (latency > 50 && latency <= 100) {
    return {
      icon: OkLatencyIcon,
      color: 'banana',
    }
  } else {
    return {
      icon: BadLatencyIcon,
      color: 'errorOpaque',
    }
  }
}

export function LatencyStatus({ latency }: { latency: string }): ReactElement {
  const latencyStatus = getLatencyStatus(+latency)

  return (
    <Flex sx={{ alignItems: 'center', gap: '1.2rem' }}>
      <TextWithBreakpointVariant
        variant={['size12Weight400Line138', 'size15Weight400Line138']}
        sx={{ color: latencyStatus.color }}
      >
        {getMsText(latency)}
      </TextWithBreakpointVariant>
      <Svg
        svg={latencyStatus.icon}
        descriptiveText={`Latency status: ${
          +latency <= 50 ? 'Good' : +latency <= 100 ? 'OK' : 'Bad'
        }`}
      />
    </Flex>
  )
}
